import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import { vehicles } from "@/mocks/vehicles";

const steps = ["Dados", "Confirmação", "Baixar App"];

export default function CheckoutPage() {
  const [searchParams] = useSearchParams();
  const vehicleId = searchParams.get("vehicleId") || "1";
  const days = Number(searchParams.get("days") || 3);
  const vehicle = vehicles.find((v) => v.id === vehicleId) || vehicles[0];
  const total = vehicle.price * days;

  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ name: "", cpf: "", phone: "", email: "", terms: false });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.name = "Nome obrigatório";
    if (!form.cpf.trim()) e.cpf = "CPF obrigatório";
    if (!form.phone.trim()) e.phone = "Telefone obrigatório";
    if (!form.email.trim()) e.email = "E-mail obrigatório";
    if (!form.terms) e.terms = "Aceite os termos para continuar";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    if (step === 0 && !validate()) return;
    setStep((s) => Math.min(s + 1, 2));
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="pt-20 max-w-3xl mx-auto px-4 md:px-8 py-10">
        {/* Stepper */}
        <div className="flex items-center justify-center gap-0 mb-10">
          {steps.map((s, i) => (
            <div key={s} className="flex items-center">
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all ${i === step ? "bg-orange-500 text-white" : i < step ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-400"}`}>
                {i < step ? <i className="ri-check-line"></i> : <span>{i + 1}</span>}
                {s}
              </div>
              {i < steps.length - 1 && <div className={`w-8 h-px ${i < step ? "bg-green-400" : "bg-gray-200"}`}></div>}
            </div>
          ))}
        </div>

        {/* Vehicle Summary */}
        <div className="bg-gray-50 rounded-2xl p-5 flex gap-4 mb-8">
          <div className="w-24 h-16 rounded-xl overflow-hidden flex-shrink-0">
            <img src={vehicle.image} alt={vehicle.name} className="w-full h-full object-cover object-top" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-gray-900">{vehicle.name}</h3>
            <p className="text-xs text-gray-500">{vehicle.category} • {days} dias</p>
          </div>
          <div className="text-right">
            <div className="text-orange-500 font-extrabold text-xl">R$ {total}</div>
            <div className="text-xs text-gray-400">R$ {vehicle.price}/dia</div>
          </div>
        </div>

        {/* Step 0: Form */}
        {step === 0 && (
          <div className="bg-white border border-gray-100 rounded-2xl p-6">
            <h2 className="font-bold text-gray-900 text-lg mb-5">Seus dados</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              {[
                { key: "name", label: "Nome completo", type: "text", placeholder: "João da Silva" },
                { key: "cpf", label: "CPF", type: "text", placeholder: "000.000.000-00" },
                { key: "phone", label: "Telefone", type: "tel", placeholder: "(19) 99999-0000" },
                { key: "email", label: "E-mail", type: "email", placeholder: "joao@email.com" },
              ].map((field) => (
                <div key={field.key}>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">{field.label}</label>
                  <input
                    type={field.type}
                    placeholder={field.placeholder}
                    value={form[field.key as keyof typeof form] as string}
                    onChange={(e) => setForm({ ...form, [field.key]: e.target.value })}
                    className={`w-full px-4 py-3 border rounded-xl text-sm focus:outline-none focus:border-orange-400 ${errors[field.key] ? "border-red-400" : "border-gray-200"}`}
                  />
                  {errors[field.key] && <p className="text-red-500 text-xs mt-1">{errors[field.key]}</p>}
                </div>
              ))}
            </div>
            <div className="bg-orange-50 rounded-xl p-4 mb-4">
              <div className="flex items-start gap-2">
                <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                  <i className="ri-information-line text-orange-500"></i>
                </div>
                <p className="text-xs text-orange-700 leading-relaxed">
                  <strong>Sem entrada!</strong> Após o cadastro, você receberá acesso ao app da LocaCar para finalizar a reserva e realizar o pagamento da fatura diretamente pelo aplicativo.
                </p>
              </div>
            </div>
            <label className="flex items-start gap-3 cursor-pointer mb-5">
              <input type="checkbox" checked={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.checked })} className="w-4 h-4 accent-orange-500 mt-0.5 cursor-pointer" />
              <span className="text-sm text-gray-600">Aceito os <a href="#" className="text-orange-500 underline">termos de uso</a> e a <a href="#" className="text-orange-500 underline">política de privacidade</a></span>
            </label>
            {errors.terms && <p className="text-red-500 text-xs mb-3">{errors.terms}</p>}
            <button onClick={handleNext} className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 rounded-xl transition-all cursor-pointer whitespace-nowrap">
              Continuar
            </button>
          </div>
        )}

        {/* Step 1: Confirmation */}
        {step === 1 && (
          <div className="bg-white border border-gray-100 rounded-2xl p-6 text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-orange-100 rounded-full mx-auto mb-4">
              <i className="ri-shield-check-line text-orange-500 text-3xl"></i>
            </div>
            <h2 className="font-bold text-gray-900 text-xl mb-2">Quase lá!</h2>
            <p className="text-gray-500 text-sm mb-6 leading-relaxed">
              Seus dados foram registrados. O pagamento é feito <strong>exclusivamente pelo app da LocaCar</strong>. Baixe o app para finalizar sua reserva.
            </p>
            <div className="bg-gray-50 rounded-xl p-4 text-left mb-6">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Resumo da reserva</p>
              <div className="flex justify-between text-sm mb-1"><span className="text-gray-600">Veículo</span><span className="font-semibold">{vehicle.name}</span></div>
              <div className="flex justify-between text-sm mb-1"><span className="text-gray-600">Período</span><span className="font-semibold">{days} dias</span></div>
              <div className="flex justify-between text-sm mb-1"><span className="text-gray-600">Nome</span><span className="font-semibold">{form.name}</span></div>
              <div className="flex justify-between text-sm border-t border-gray-200 pt-2 mt-2"><span className="text-gray-600 font-bold">Total</span><span className="font-extrabold text-orange-500">R$ {total}</span></div>
            </div>
            <button onClick={handleNext} className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 rounded-xl transition-all cursor-pointer whitespace-nowrap">
              Finalizar Cadastro
            </button>
          </div>
        )}

        {/* Step 2: Download App */}
        {step === 2 && (
          <div className="bg-white border border-gray-100 rounded-2xl p-8 text-center">
            <div className="w-20 h-20 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
              <i className="ri-check-double-line text-green-500 text-4xl"></i>
            </div>
            <h2 className="font-extrabold text-gray-900 text-2xl mb-2">Reserva Confirmada!</h2>
            <p className="text-gray-500 text-sm mb-2">Número do pedido: <strong className="text-gray-900">#LC{Math.floor(Math.random() * 90000) + 10000}</strong></p>
            <p className="text-gray-500 text-sm mb-8 leading-relaxed">
              Baixe o app da LocaCar para visualizar sua reserva, acompanhar o status e realizar o pagamento da fatura.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
              <a href="#" className="flex items-center gap-3 bg-black text-white px-6 py-3.5 rounded-xl hover:bg-gray-900 transition-all cursor-pointer justify-center">
                <div className="w-6 h-6 flex items-center justify-center"><i className="ri-apple-fill text-xl"></i></div>
                <div className="text-left"><div className="text-xs text-white/70">Disponível na</div><div className="text-sm font-bold">App Store</div></div>
              </a>
              <a href="#" className="flex items-center gap-3 bg-black text-white px-6 py-3.5 rounded-xl hover:bg-gray-900 transition-all cursor-pointer justify-center">
                <div className="w-6 h-6 flex items-center justify-center"><i className="ri-google-play-fill text-xl"></i></div>
                <div className="text-left"><div className="text-xs text-white/70">Disponível no</div><div className="text-sm font-bold">Google Play</div></div>
              </a>
            </div>
            <Link to="/" className="text-orange-500 text-sm font-semibold hover:underline cursor-pointer">Voltar para a página inicial</Link>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
